import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { Autorenew as BackgroundIcon } from '@mui/icons-material';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import { SwitchMode } from '../../../types/backend';
import type { SwitchDetail, SwitchState } from '../../../types/backend';
import { describeState, summarizeState } from './featureSwitchForm';

const CHIP_COLOR: Record<SwitchState['mode'], 'default' | 'success' | 'info'> = {
  [SwitchMode.OFF]: 'default',
  [SwitchMode.EVERYONE]: 'success',
  [SwitchMode.GROUPS]: 'info',
};

/** Who the switch is enabled for, as a chip, with a marker when background work is on too. */
const SwitchStatus: React.FC<{ detail: SwitchDetail }> = ({ detail }) => {
  // The generated types drop the backend's `| None` on state.
  const state: SwitchState | null = detail.state ?? null;
  const testId = `feature-switch-status-${detail.name}`;
  if (!state) {
    return <Chip size="small" color="warning" label="Unreadable" data-testid={testId} sx={{ ml: 1 }} />;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
      {state.background && (
        <Tooltip title="Background work on">
          <BackgroundIcon fontSize="small" aria-label="Background work on" aria-hidden={false} />
        </Tooltip>
      )}
      <Tooltip title={describeState(state)}>
        <Chip
          size="small"
          color={CHIP_COLOR[state.mode]}
          variant={state.mode === SwitchMode.OFF ? 'outlined' : 'filled'}
          label={summarizeState(state)}
          data-testid={testId}
        />
      </Tooltip>
    </Box>
  );
};

const LIST_CONFIG = {
  getPrimaryText: (d: SwitchDetail) => d.name,
  getSecondaryText: (d: SwitchDetail) => d.description,
  getId: (d: SwitchDetail) => d.name,
  secondaryTypographyProps: { noWrap: true },
  getBadge: (d: SwitchDetail) => <SwitchStatus detail={d} />,
  getTestId: (d: SwitchDetail) => `feature-switch-row-${d.name}`,
};

interface Props {
  details: SwitchDetail[];
  selectedName: string | null;
  onSelect: (name: string) => void;
}

/** The master list of the Feature Switches tab: one row per switch with who it is enabled for. */
const FeatureSwitchList: React.FC<Props> = ({ details, selectedName, onSelect }) => (
  <EntityListPanel
    entities={details}
    selectedEntity={details.find((d) => d.name === selectedName) ?? null}
    isNew={false}
    saving={false}
    config={LIST_CONFIG}
    emptyStateText="No feature switches are declared"
    emptyStateHelperText="The server declares no switches"
    onSelect={(d) => onSelect(d.name)}
  />
);

export default FeatureSwitchList;
